import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { FeatureProvider } from "./context/FeatureContext";
import FeatureGate from "./components/FeatureGate";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AdminDashboard from "./pages/AdminDashboard";
import AdminMerchants from "./pages/AdminMerchants";
import AdminPhotos from "./pages/AdminPhotos";
import AdminReservations from "./pages/AdminReservations";
import AdminDeleted from "./pages/AdminDeleted";
import AdminMarketAlerts from "./pages/AdminMarketAlerts";
import AdminStories from "./pages/AdminStories";
import MerchantDashboard from "./pages/MerchantDashboard";
import UploadPhoto from "./pages/UploadPhoto";
import MyPhotos from "./pages/MyPhotos";
import Reservations from "./pages/Reservations";
import SearchPage from "./pages/Search";
import Messages from "./pages/Messages";
import ChangePassword from "./pages/ChangePassword";
import Profile from "./pages/Profile";
import MerchantInsights from "./pages/MerchantInsights";
import AdminNameRequests from "./pages/AdminNameRequests";
import FounderDashboard from "./pages/FounderDashboard";
import Invoices from "./pages/Invoices";
import ContactAdmin from "./pages/ContactAdmin";
import AdminMessages from "./pages/AdminMessages";
import AdminWhatsApp from "./pages/AdminWhatsApp";
import ExternalSourcing from "./pages/ExternalSourcing";
import PublicStore from "./pages/PublicStore";
import ShareStore from "./pages/ShareStore";
import Employees from "./pages/Employees";
import Branches from "./pages/Branches";
import EmployeeLeaves from "./pages/EmployeeLeaves";
import PhysicalSales from "./pages/PhysicalSales";
import StoreOrders from "./pages/StoreOrders";
import Marketplace from "./pages/Marketplace";
import Debts from "./pages/Debts";
import Returns from "./pages/Returns";
import MarketingVideos from "./pages/MarketingVideos";
import { Toaster } from "sonner";

const withLayout = (el) => <Layout>{el}</Layout>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
        <FeatureProvider>
          <Toaster position="top-center" richColors closeButton dir="rtl" mobileOffset={{ top: 76 }} />
          <div className="App">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/stores" element={<Marketplace />} />
              <Route path="/store/:merchantId" element={<PublicStore />} />

            <Route path="/dashboard" element={<ProtectedRoute role="merchant">{withLayout(<MerchantDashboard />)}</ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<UploadPhoto />)}</ProtectedRoute>} />
            <Route path="/my-photos" element={<ProtectedRoute role="merchant">{withLayout(<MyPhotos />)}</ProtectedRoute>} />
            <Route path="/reservations" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<Reservations />)}</ProtectedRoute>} />
            <Route path="/search" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<SearchPage />)}</ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<Messages />)}</ProtectedRoute>} />
            <Route path="/messages/:reservationId" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<Messages />)}</ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute role="merchant">{withLayout(<Profile />)}</ProtectedRoute>} />
            <Route path="/insights" element={<ProtectedRoute role="merchant">{withLayout(<FeatureGate feature="insights"><MerchantInsights /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/contact-admin" element={<ProtectedRoute role="merchant">{withLayout(<ContactAdmin />)}</ProtectedRoute>} />
            <Route path="/messages/external-sourcing" element={<ProtectedRoute role="merchant">{withLayout(<FeatureGate feature="external_sourcing"><ExternalSourcing /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/share-store" element={<ProtectedRoute role="merchant">{withLayout(<FeatureGate feature="public_store"><ShareStore /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/employees" element={<ProtectedRoute role="merchant">{withLayout(<Employees />)}</ProtectedRoute>} />
            <Route path="/branches" element={<ProtectedRoute role="merchant">{withLayout(<Branches />)}</ProtectedRoute>} />
            <Route path="/employee/leaves" element={<ProtectedRoute role="employee">{withLayout(<EmployeeLeaves />)}</ProtectedRoute>} />
            <Route path="/sales" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<FeatureGate feature="physical_sales"><PhysicalSales /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/store-orders" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<FeatureGate feature="public_store"><StoreOrders /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/debts" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<FeatureGate feature="debt"><Debts /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/marketing-videos" element={<ProtectedRoute role="merchant">{withLayout(<FeatureGate feature="marketing_videos"><MarketingVideos /></FeatureGate>)}</ProtectedRoute>} />
            <Route path="/returns" element={<ProtectedRoute role={["merchant", "employee"]}>{withLayout(<FeatureGate feature="returns"><Returns /></FeatureGate>)}</ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute role="admin">{withLayout(<AdminDashboard />)}</ProtectedRoute>} />
            <Route path="/admin/merchants" element={<ProtectedRoute role="admin">{withLayout(<AdminMerchants />)}</ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute role="admin">{withLayout(<AdminMessages />)}</ProtectedRoute>} />
            <Route path="/admin/whatsapp" element={<ProtectedRoute role="admin">{withLayout(<AdminWhatsApp />)}</ProtectedRoute>} />
            <Route path="/admin/photos" element={<ProtectedRoute role="admin">{withLayout(<AdminPhotos />)}</ProtectedRoute>} />
            <Route path="/admin/reservations" element={<ProtectedRoute role="admin">{withLayout(<AdminReservations />)}</ProtectedRoute>} />
            <Route path="/admin/deleted" element={<ProtectedRoute role="admin">{withLayout(<AdminDeleted />)}</ProtectedRoute>} />
            <Route path="/admin/market-alerts" element={<ProtectedRoute role="admin">{withLayout(<AdminMarketAlerts />)}</ProtectedRoute>} />
            <Route path="/admin/stories" element={<ProtectedRoute role="admin">{withLayout(<AdminStories />)}</ProtectedRoute>} />
            <Route path="/admin/name-requests" element={<ProtectedRoute role="admin">{withLayout(<AdminNameRequests />)}</ProtectedRoute>} />
            <Route path="/admin/founder" element={<ProtectedRoute role="admin">{withLayout(<FounderDashboard />)}</ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute role={["merchant", "admin", "employee"]}>{withLayout(<Invoices />)}</ProtectedRoute>} />

            <Route path="/change-password" element={
              <ProtectedRoute>{withLayout(<ChangePassword />)}</ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </FeatureProvider>
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
